import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { i18n } from '@/locale/i18n'
import { questionDetailUrl, keepQueryString } from '@/urls'
import MarketVolumeStat from '@/features/market/question-footer/MarketVolumeStat'
import RowBetCountToggle from './RowBetCountToggle'
import { OUTCOME_LINES_WIDTH } from '@/features/market/outcome-list/outcomePointer'
import { hasCryptoPriceTarget } from '@/features/market/utils/cryptoEvent'
import CryptoAssetBadge, { hasCryptoAssetBadge } from '@/features/market/crypto-asset-badge'
import CryptoPriceStats from '@/features/market/crypto-price-stats'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** Past this the second line gives the year rather than a count of days nobody reads. */
const COUNT_DAYS_UP_TO = 60 * DAY

/**
 * Milliseconds left on the market, or null before the first client tick. Null on that
 * first pass on purpose: the feed is server-rendered, and counting during render would
 * ship server-clock digits the client immediately disagrees with — the row shows the
 * date alone until the effect runs.
 *
 * The interval follows the figure on screen: seconds inside the last hour, minutes
 * above it, and no timer at all once the deadline is more than a day out, so a feed of
 * long-dated markets runs no clocks.
 */
const useTimeLeft = endDate => {
  const endTS = Date.parse(endDate ?? '')
  const [now, setNow] = useState(null)

  const remaining = now === null || !Number.isFinite(endTS) ? null : endTS - now
  const ticking = remaining === null || (remaining > 0 && remaining <= DAY)
  const period = remaining !== null && remaining <= HOUR ? SECOND : MINUTE

  useEffect(() => {
    if (!Number.isFinite(endTS)) return undefined
    setNow(Date.now())
    if (!ticking) return undefined

    const id = setInterval(() => setNow(Date.now()), period)
    return () => clearInterval(id)
  }, [endTS, ticking, period])

  return remaining
}

/**
 * One market on a single line, read as a table row: when it closes on the left, the
 * title, its volume, its outcomes as text on the right — a label and a probability a
 * line, supplied by the caller. The counterpart of SportEventCompactRow for every
 * market that is not a fixture.
 */
const MarketCompactRow = ({
  question,
  outcomes,
  isRealMoney = false,
  betListOpen = false,
  onToggleBetList,
}) => {
  /* Crypto price rows carry the asset tile and are titled by their prices
     ("ETH 1 hour: To beat … | Current: …"); the backend title stays as the tooltip. */
  const isCryptoRow = hasCryptoPriceTarget(question)
  const title = question?.title
  const betEnd = question?.bet_end_date ? dayjs(question.bet_end_date) : null
  const timeLeft = useTimeLeft(question?.bet_end_date)

  /* Always the same two lines, so the column reads as a column: the date on top, and
     under it how far off it is — counting down inside the last day, as the card's own
     bet end date does, in days up to two months, and the year past that. Only the
     second line changes colour as the deadline closes in, at the market page's own
     thresholds. */
  const renderDateColumn = () => {
    if (!betEnd?.isValid()) return <div className="text-sm font-semibold text-Neutral-4">-</div>

    const pad = value => String(value).padStart(2, '0')
    let distance = null
    let tone = 'text-Neutral-4'

    if (timeLeft !== null && timeLeft > 0) {
      if (timeLeft <= HOUR) {
        distance = `${pad(Math.floor(timeLeft / MINUTE))}:${pad(Math.floor((timeLeft % MINUTE) / SECOND))}`
        tone = timeLeft < MINUTE ? 'text-negative-4' : timeLeft <= 5 * MINUTE ? 'text-warning-4' : 'text-Neutral-6'
      } else if (timeLeft <= DAY) {
        distance = `${Math.floor(timeLeft / HOUR)}${i18n('markets.hours_short')}`
        tone = 'text-Neutral-6'
      } else if (timeLeft <= COUNT_DAYS_UP_TO) {
        distance = `${Math.round(timeLeft / DAY)}${i18n('markets.days_short')}`
      }
    }
    if (distance === null && !betEnd.isSame(dayjs(), 'year')) distance = betEnd.format('YYYY')

    return (
      <>
        <div className="text-sm font-semibold tabular-nums text-Neutral-6">{betEnd.format('DD MMM')}</div>
        {distance !== null && <div className={`text-xs tabular-nums ${tone}`}>{distance}</div>}
      </>
    )
  }

  /* `items-start` with every cell centred inside its own 48px: the outcome column can
     grow extra lines when the row is expanded, and the date and the title stay exactly
     where they were on the first line. */
  return (
    <div className="flex flex-nowrap items-start gap-x-4 px-4 py-3">
      {/* The digits alone say nothing about what they count down to, and the column
          header that does is decoration a screen reader never reaches. Each cell
          carries its own heading instead. */}
      <div
        className="flex h-12 w-14 shrink-0 flex-col items-center justify-center text-center"
        title={betEnd?.isValid() ? betEnd.format('DD MMM YYYY, HH:mm') : undefined}>
        {betEnd?.isValid() && (
          <span className="sr-only">
            {i18n('markets.closes_on_label', { date: betEnd.format('DD MMM YYYY, HH:mm') })}
          </span>
        )}
        <div aria-hidden className="flex flex-col items-center">
          {renderDateColumn()}
        </div>
      </div>

      {isCryptoRow && hasCryptoAssetBadge(question) && (
        <div className="flex h-12 shrink-0 items-center">
          <CryptoAssetBadge question={question} className="w-8 h-8" />
        </div>
      )}

      <div className="flex min-h-12 min-w-0 flex-1 flex-col justify-center gap-1">
        {/* The title is the row's way to the market page, and its only keyboard stop:
            clicking the rest of the row aims the bet pad instead. The card's click
            handler stands down for nested anchors, so the two never both fire. */}
        <Link to={keepQueryString(questionDetailUrl(question))}>
          {/* The styling sits on the element inside, as the card's own title link does:
              common.scss sets `color: inherit` and `text-decoration: none` on every `a`
              unlayered, which outranks any utility put on the anchor itself. */}
          <div
            className="question-card__row-title text-sm font-medium leading-[130%] text-Neutral-6 line-clamp-2 hover:underline"
            title={title}>
            {isCryptoRow ? <CryptoPriceStats question={question} className="font-semibold" /> : title}
          </div>
        </Link>

        {/* Under the title, whether the reader holds a position in it — a control, since
            it opens those positions. Renders nothing when they hold none. */}
        <RowBetCountToggle question={question} open={betListOpen} onToggle={onToggleBetList} />
      </div>

      {/* Its own column, under the header that names it, so the figures line up down the
          list instead of trailing each title at whatever length that title ended. They
          start at the cell's left edge, and the inset before it is the distance from the
          title — a column wide enough to carry that distance and still left-align. No
          `ml-auto`: the title block is `flex-1` and leaves no slack for one to take. */}
      <div className="flex h-12 w-32 shrink-0 items-center justify-start pl-8">
        <MarketVolumeStat question={question} isRealMoney={isRealMoney} />
      </div>

      {/* The width lives here rather than on the block inside: a market whose outcomes
          render nothing would otherwise drag the cells before it across. */}
      <div className={`flex min-h-12 shrink-0 items-center ${OUTCOME_LINES_WIDTH}`}>{outcomes}</div>
    </div>
  )
}

export default MarketCompactRow
